import type { Area } from 'react-easy-crop';

const OUTPUT_JPEG_QUALITY = 0.92;

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () =>
      reject(new Error('Could not load the image for editing'))
    );
    image.src = source;
  });
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function rotatedBoundingBox(width: number, height: number, rotation: number) {
  const radians = toRadians(rotation);
  return {
    width:
      Math.abs(Math.cos(radians) * width) +
      Math.abs(Math.sin(radians) * height),
    height:
      Math.abs(Math.sin(radians) * width) +
      Math.abs(Math.cos(radians) * height),
  };
}

function outputTypeFor(sourceType: string): string {
  if (sourceType === 'image/jpeg' || sourceType === 'image/jpg') {
    return 'image/jpeg';
  }
  if (sourceType === 'image/webp') {
    return 'image/webp';
  }
  return 'image/png';
}

function extensionFor(outputType: string): string {
  if (outputType === 'image/jpeg') return 'jpg';
  if (outputType === 'image/webp') return 'webp';
  return 'png';
}

function fileNameFor(sourceName: string, outputType: string): string {
  const base = sourceName.replace(/\.[^./\\]+$/, '') || 'image';
  return `${base}.${extensionFor(outputType)}`;
}

export async function getCroppedImage(
  imageSrc: string,
  cropAreaPixels: Area,
  rotation: number,
  sourceType: string,
  sourceName: string
): Promise<File> {
  const image = await loadImage(imageSrc);

  const rotatedCanvas = document.createElement('canvas');
  const rotatedContext = rotatedCanvas.getContext('2d');
  if (!rotatedContext) throw new Error('Could not get a 2D canvas context');

  const boundingBox = rotatedBoundingBox(image.width, image.height, rotation);
  rotatedCanvas.width = boundingBox.width;
  rotatedCanvas.height = boundingBox.height;

  rotatedContext.translate(boundingBox.width / 2, boundingBox.height / 2);
  rotatedContext.rotate(toRadians(rotation));
  rotatedContext.drawImage(image, -image.width / 2, -image.height / 2);

  const croppedCanvas = document.createElement('canvas');
  const croppedContext = croppedCanvas.getContext('2d');
  if (!croppedContext) throw new Error('Could not get a 2D canvas context');

  croppedCanvas.width = Math.round(cropAreaPixels.width);
  croppedCanvas.height = Math.round(cropAreaPixels.height);

  croppedContext.drawImage(
    rotatedCanvas,
    Math.round(cropAreaPixels.x),
    Math.round(cropAreaPixels.y),
    croppedCanvas.width,
    croppedCanvas.height,
    0,
    0,
    croppedCanvas.width,
    croppedCanvas.height
  );

  const outputType = outputTypeFor(sourceType);

  const blob = await new Promise<Blob | null>((resolve) => {
    croppedCanvas.toBlob(resolve, outputType, OUTPUT_JPEG_QUALITY);
  });
  if (!blob) throw new Error('Could not export the edited image');

  return new File([blob], fileNameFor(sourceName, outputType), {
    type: outputType,
  });
}
